// SPDX-License-Identifier: AGPL-3.0-or-later

import VoiceChannelInterest from '@app/features/voice/state/VoiceChannelInterest';
import {useEffect, useRef} from 'react';

export type VoiceChannelListInterestEntry = readonly [channelId: string, scrollId: string];

const VISIBLE_MARGIN = '50% 0px';

export function useVoiceChannelListInterest(
	getScrollContainer: () => HTMLElement | null,
	guildId: string,
	entries: ReadonlyArray<VoiceChannelListInterestEntry>,
): void {
	const enabled = VoiceChannelInterest.enabled;
	const entriesRef = useRef(entries);
	entriesRef.current = entries;
	const entriesKey = enabled ? entries.map(([channelId, scrollId]) => `${channelId}:${scrollId}`).join(',') : '';
	useEffect(() => {
		if (!enabled) return;
		const currentEntries = entriesRef.current;
		const publish = (isVisible: (scrollId: string) => boolean) =>
			VoiceChannelInterest.setListChannels(
				guildId,
				currentEntries.filter(([, scrollId]) => isVisible(scrollId)).map(([channelId]) => channelId),
			);
		const container = getScrollContainer();
		if (container == null || typeof IntersectionObserver === 'undefined') {
			publish(() => true);
			return () => VoiceChannelInterest.setListChannels(guildId, []);
		}
		const scrollIds = new Set(currentEntries.map(([, scrollId]) => scrollId));
		const visibleScrollIds = new Set<string>();
		const observer = new IntersectionObserver(
			(records) => {
				for (const record of records) {
					const scrollId = (record.target as HTMLElement).dataset.scrollId;
					if (scrollId == null) continue;
					if (record.isIntersecting) {
						visibleScrollIds.add(scrollId);
					} else {
						visibleScrollIds.delete(scrollId);
					}
				}
				publish((scrollId) => visibleScrollIds.has(scrollId));
			},
			{root: container, rootMargin: VISIBLE_MARGIN},
		);
		for (const node of container.querySelectorAll<HTMLElement>('[data-scroll-id]')) {
			if (scrollIds.has(node.dataset.scrollId ?? '')) observer.observe(node);
		}
		return () => {
			observer.disconnect();
			VoiceChannelInterest.setListChannels(guildId, []);
		};
	}, [enabled, guildId, entriesKey, getScrollContainer]);
}

export function useRetainedVoiceChannel(
	guildId: string | null | undefined,
	channelId: string | null | undefined,
): void {
	const enabled = VoiceChannelInterest.enabled;
	useEffect(() => {
		if (!enabled || guildId == null || channelId == null) return;
		return VoiceChannelInterest.retainChannel(guildId, channelId);
	}, [enabled, guildId, channelId]);
}
